import { useState } from "react";
import { useClientFiles } from "@/hooks/useClientFiles";
import { useAuth } from "@/context/AuthContext";
import { FileRow } from "@/components/FileRow";
import { ImageLightbox } from "@/components/ImageLightbox";
import { DocTypeDialog } from "@/components/DocTypeDialog";
import { fileCategory } from "@/lib/files";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription,
  AlertDialogFooter, AlertDialogCancel, AlertDialogAction,
} from "@/components/ui/alert-dialog";
import { Paperclip, Upload, Loader2, RefreshCw } from "lucide-react";

export function ClientFiles({ clientId }) {
  const { files, uploading, uploadItems, syncing, syncFromSharePoint, dragActive, fileInputRef, onInputChange, dragHandlers,
          pendingFiles, confirmUpload, cancelUpload, open, download, recategorize, remove } = useClientFiles(clientId);
  const { user } = useAuth();
  const canDelete = Boolean(user?.can_delete_files);
  const canUpload = user?.can_upload_files !== false;
  const canView = user?.can_view_files !== false;
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const imageFiles = files.filter((f) => fileCategory(f.original_filename, f.content_type) === "image");
  const openLightbox = (file) => {
    const idx = imageFiles.findIndex((f) => f.id === file.id);
    if (idx >= 0) setLightboxIndex(idx);
  };

  const dropzoneClass = dragActive
    ? "border-[#002FA7] bg-blue-50 text-[#002FA7]"
    : "border-gray-200 hover:border-[#002FA7] hover:bg-blue-50/40 text-gray-600 hover:text-[#002FA7]";

  const label = uploading ? "Uploading…" : dragActive ? "Drop files to upload" : "Drag & drop or click to upload";

  return (
    <div className="bg-white border border-gray-200 rounded-sm shadow-sm p-6 mt-6" data-testid="files-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading text-lg font-bold text-gray-900 flex items-center gap-2">
          <Paperclip className="w-4 h-4 text-gray-400" /> Files
        </h3>
        <div className="flex items-center gap-2">
          <button onClick={syncFromSharePoint} disabled={syncing} data-testid="sync-sharepoint-button"
            title="Import files added directly in SharePoint"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-gray-500 hover:text-[#0078D4] border border-gray-200 hover:border-[#0078D4] px-2 py-1 rounded-sm transition-colors disabled:opacity-60">
            {syncing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
            {syncing ? "Syncing…" : "Sync from SharePoint"}
          </button>
          <span className="text-xs text-gray-400">{files.length}</span>
        </div>
      </div>

      <input ref={fileInputRef} type="file" multiple onChange={onInputChange} className="hidden" data-testid="file-input" />
      {canUpload ? (
        <div
          {...dragHandlers}
          onClick={() => !uploading && fileInputRef.current?.click()}
          data-testid="upload-dropzone"
          className={`cursor-pointer w-full flex flex-col items-center justify-center gap-1 border-2 border-dashed px-4 py-6 rounded-sm font-semibold text-sm transition-colors ${dropzoneClass} ${uploading ? "opacity-60 pointer-events-none" : ""}`}
        >
          <button type="button" data-testid="upload-file-button" className="flex items-center gap-2" tabIndex={-1}>
            {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
            {label}
          </button>
          <span className="text-xs font-normal text-gray-400">JPEG, PDF, Word, Excel, PowerPoint, email · up to 100 MB</span>
        </div>
      ) : (
        <p className="text-xs text-gray-400 border border-dashed border-gray-200 rounded-sm px-4 py-4 text-center" data-testid="upload-disabled-note">
          Your group doesn't have permission to upload files.
        </p>
      )}

      {uploadItems.length > 0 && (
        <div className="mt-3 space-y-2" data-testid="upload-progress-list">
          {uploadItems.map((u) => (
            <div key={u.id} data-testid={`upload-progress-${u.name}`} className="text-xs">
              <div className="flex items-center justify-between mb-1">
                <span className="text-gray-600 truncate pr-2">{u.name}</span>
                <span className="font-semibold text-[#002FA7] tabular-nums shrink-0">{u.percent}%</span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full bg-[#002FA7] rounded-full transition-all duration-200" style={{ width: `${u.percent}%` }} />
              </div>
            </div>
          ))}
        </div>
      )}

      {files.length > 0 && (
        <div className="mt-4 space-y-2" data-testid="files-list">
          {files.map((f) => (
            <FileRow key={f.id} file={f} onOpen={open} onDownload={download} onDelete={setDeleteTarget}
              onThumbClick={openLightbox} onRecategorize={recategorize} canDelete={canDelete} canView={canView} />
          ))}
        </div>
      )}

      <DocTypeDialog open={Boolean(pendingFiles)} files={pendingFiles || []} onConfirm={confirmUpload} onCancel={cancelUpload} />

      <ImageLightbox images={imageFiles} index={lightboxIndex} onIndexChange={setLightboxIndex} onClose={() => setLightboxIndex(null)} />

      <AlertDialog open={Boolean(deleteTarget)} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="rounded-sm" data-testid="delete-file-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Remove file?</AlertDialogTitle>
            <AlertDialogDescription>
              <strong>{deleteTarget?.original_filename}</strong> will be removed from this client.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-sm">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => { remove(deleteTarget.id); setDeleteTarget(null); }}
              data-testid="confirm-delete-file-button" className="bg-red-600 hover:bg-red-700 rounded-sm">Remove</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
